import React from 'react'
import { AlertTriangle, Link2Off, ArrowRight } from 'lucide-react'
import { Link } from '@inertiajs/react'
import { severanceWarning, type SeveredRoom } from '@/lib/groups'

interface SeveranceNoticeProps {
  severed: SeveredRoom[]
  /** 'before' spells out what WILL happen; 'after' reports what DID. */
  mode: 'before' | 'after'
  /** The listing(s) leaving, named in the explanation so the sentence has a subject. */
  subject: string
}

// THE CONSEQUENCE OF A LISTING LEAVING A COMBINED INVENTORY, IN PLAIN LANGUAGE (Groups rule 4, reused
// by combined inventory rule 10).
//
// A room may take its price from a room at ANOTHER listing: a linked child from its parent, a Same
// room from its twin. That only works while both listings are priced in one pass, so the link is
// broken when they stop being priced together and each room becomes independent, and an independent
// room with no base rate is not priced at all.
//
// The notice is deliberately three different things, because a destructive confirmation that
// overstates its consequence teaches the operator to ignore the next one:
//   * nothing severed         → this component renders nothing
//   * severed, rates in place → a calm informational note: they price on their own, nothing goes dark
//   * severed and unpriced    → the warning, with the exact count and every room named
//
// Rooms are NAMED on both sides of the broken link ("Annex Standard — follows Lead Standard at
// Harbour Lead Hotel"), because "3 rooms will become independent" is not enough to confirm on.
export default function SeveranceNotice({ severed, mode, subject }: SeveranceNoticeProps) {
  const warning = severanceWarning(severed)
  if (warning.tone === 'none') return null

  const isWarning = warning.tone === 'warning'
  const Icon = isWarning ? AlertTriangle : Link2Off

  return (
    <div
      role={isWarning && mode === 'before' ? 'alert' : undefined}
      className={
        isWarning
          ? 'rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3'
          : 'rounded-lg border border-border/60 bg-muted/40 px-3.5 py-3'
      }
    >
      <p
        className={`flex items-start gap-2 text-sm font-semibold ${
          isWarning ? 'text-amber-900' : 'text-foreground'
        }`}
      >
        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${isWarning ? 'text-amber-600' : 'text-muted-foreground'}`} />
        <span>{mode === 'after' ? afterHeadline(warning.unpriced.length, warning.rooms.length) : warning.headline}</span>
      </p>

      <p className={`mt-2 pl-6 text-xs leading-relaxed ${isWarning ? 'text-amber-800' : 'text-muted-foreground'}`}>
        {mode === 'before' ? (
          <>
            These rooms take their price from a room at another listing. That only works while both listings are
            priced together, so the link is broken when {subject} leaves and each room prices on its own from a base
            rate.
            {isWarning && <> Until you set one, {' '}nothing new is computed or published for it.</>}
          </>
        ) : (
          <>
            These rooms took their price from a room at another listing. That link is gone, so each one now prices on
            its own from a base rate.
            {isWarning && <> Set one to bring them back into pricing.</>}
          </>
        )}
      </p>

      <ul className="mt-2.5 space-y-2 pl-6">
        {warning.rooms.map((room) => (
          <li key={room.roomTypeId} className="text-xs">
            {/* Line 1: the room, the listing it is on, and whether it goes dark. */}
            <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <span className={`font-medium ${isWarning && room.willBeUnpriced ? 'text-amber-900' : 'text-foreground'}`}>
                {room.roomTypeName}
              </span>
              <span className={isWarning ? 'text-amber-600/60' : 'text-muted-foreground/60'} aria-hidden="true">
                ·
              </span>
              <span className={isWarning ? 'text-amber-800/80' : 'text-muted-foreground'}>{room.propertyName}</span>
              {room.willBeUnpriced && (
                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                  {mode === 'after' ? 'needs a base rate' : 'no base rate'}
                </span>
              )}
            </span>

            {/* Line 2: the link that is being broken, on its own line so it cannot read as part of
                the room's own name. A Same room is named as one, never as "following". */}
            {room.parentRoomTypeName && (
              <span className={`mt-0.5 block ${isWarning ? 'text-amber-700/80' : 'text-muted-foreground'}`}>
                {linkPhrase(room.link, mode)} {room.parentRoomTypeName}
                {room.parentPropertyName ? ` at ${room.parentPropertyName}` : ''}
              </span>
            )}

            {/* A Same room whose own link to a room on its listing comes back with it. */}
            {room.restoredParentRoomTypeName && (
              <span className={`mt-0.5 block ${isWarning ? 'text-amber-700/80' : 'text-muted-foreground'}`}>
                {mode === 'after' ? 'Follows' : 'Will follow'} {room.restoredParentRoomTypeName} again, as before
              </span>
            )}

            {/* A Same room's offset from its twin goes with it: the server's sentence, in this notice's tense. */}
            {room.offsetNote && (
              <span className={`mt-0.5 block ${isWarning ? 'text-amber-700/80' : 'text-muted-foreground'}`}>
                {room.offsetNote}
              </span>
            )}

            {mode === 'after' && room.willBeUnpriced && (
              <Link
                href={`${room.propertyBasePath}/pricing-rules/base-rates`}
                className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
              >
                Set base rates at {room.propertyName}
                <ArrowRight className="h-3 w-3" />
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

function linkPhrase(link: SeveredRoom['link'], mode: 'before' | 'after'): string {
  if (link === 'same_room') return mode === 'after' ? 'was the Same room as' : 'Same room as'
  return mode === 'after' ? 'was following' : 'follows'
}

function afterHeadline(unpriced: number, total: number): string {
  if (unpriced === 0) {
    return `${total === 1 ? '1 room' : `${total} rooms`} now prices on its own.`
  }
  return `${unpriced === 1 ? '1 room needs' : `${unpriced} rooms need`} a base rate before ${
    unpriced === 1 ? 'it prices' : 'they price'
  } again.`
}
